import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const LEGACY_MONTH_STORAGE_KEY = 'vylo_selected_month';
const MONTH_STORAGE_PREFIX = 'vylo_selected_month:v2';
const MONTH_STORAGE_KEY = MONTH_STORAGE_PREFIX;

const getCurrentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const normalizeMonthKey = (value) => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}$/.test(trimmed)) return null;
  const [year, month] = trimmed.split('-').map(Number);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
  return `${year}-${String(month).padStart(2, '0')}`;
};

const buildMonthStorageKey = (scopeKey) => {
  if (!scopeKey) return null;
  return `${MONTH_STORAGE_PREFIX}:${String(scopeKey)}`;
};

const readInitialMonth = (scopeKey) => {
  if (typeof window === 'undefined') return getCurrentMonthKey();
  const storageKey = buildMonthStorageKey(scopeKey);
  if (!storageKey) return getCurrentMonthKey();
  const saved = normalizeMonthKey(window.localStorage.getItem(storageKey));
  return saved || getCurrentMonthKey();
};

const MonthContext = createContext(null);

export const MonthProvider = ({ children, scopeKey = null }) => {
  const [monthState, setMonthState] = useState(() => ({
    scopeKey,
    selectedMonth: readInitialMonth(scopeKey),
  }));
  const selectedMonth = monthState.scopeKey === scopeKey
    ? monthState.selectedMonth
    : readInitialMonth(scopeKey);

  useEffect(() => {
    const scopedMonth = readInitialMonth(scopeKey);
    setMonthState({
      scopeKey,
      selectedMonth: scopedMonth,
    });
    if (typeof window !== 'undefined' && scopeKey) {
      const storageKey = buildMonthStorageKey(scopeKey);
      window.localStorage.setItem(storageKey, scopedMonth);
      window.localStorage.removeItem(LEGACY_MONTH_STORAGE_KEY);
    }
  }, [scopeKey]);

  const setSelectedMonth = useCallback((next) => {
    setMonthState((prev) => {
      const activeMonth = prev.scopeKey === scopeKey
        ? prev.selectedMonth
        : readInitialMonth(scopeKey);
      const resolved = typeof next === 'function' ? next(activeMonth) : next;
      const normalized = normalizeMonthKey(resolved) || activeMonth;
      if (typeof window !== 'undefined' && scopeKey) {
        const storageKey = buildMonthStorageKey(scopeKey);
        window.localStorage.setItem(storageKey, normalized);
        window.localStorage.removeItem(LEGACY_MONTH_STORAGE_KEY);
      }
      return {
        scopeKey,
        selectedMonth: normalized,
      };
    });
  }, [scopeKey]);

  const value = useMemo(() => ({
    selectedMonth,
    setSelectedMonth
  }), [selectedMonth, setSelectedMonth]);

  return (
    <MonthContext.Provider value={value}>
      {children}
    </MonthContext.Provider>
  );
};

export const useMonthContext = () => {
  const context = useContext(MonthContext);
  if (!context) throw new Error('useMonthContext must be used within a MonthProvider');
  return context;
};

export {
  LEGACY_MONTH_STORAGE_KEY,
  MONTH_STORAGE_KEY,
  MONTH_STORAGE_PREFIX,
  buildMonthStorageKey,
};
